import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🖼 Client-side compression — max 1280px JPEG so pics fly through the proxy.
export const compressImage = (file, maxDim = 1280, quality = 0.82) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

// 📸 Snap-or-pick a deal pic, caption it, POG it to the feed (+POG BUCKS).
export const PicComposer = ({ open, onClose, onPosted }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [deal, setDeal] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = (f) => {
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const post = async () => {
    if (!file) { toast("Snap or pick a pic first! 📸"); return; }
    if (caption.trim().length < 2) { toast("Give your pic a caption!"); return; }
    setBusy(true);
    try {
      const pos = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition((p) => res(p.coords), () => res(null), { timeout: 4000 });
      });
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append("file", blob, "pic.jpg");
      fd.append("caption", caption.trim());
      fd.append("deal_text", deal.trim());
      if (pos) { fd.append("lat", pos.latitude); fd.append("lng", pos.longitude); }
      const r = await axios.post(`${API}/flics`, fd, { withCredentials: true });
      toast.success(r.data.message || "PIC POG'D! 📸🔥");
      pick(null); setCaption(""); setDeal("");
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload hiccup — give it another POG!");
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[2200] bg-black/70 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div data-testid="pic-composer" onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
            className="w-full sm:max-w-md bg-slate-900 border border-amber-400/30 rounded-t-3xl sm:rounded-3xl p-5 text-white">
            <div className="flex items-center justify-between">
              <p className="font-heading font-black text-lg">📸 POST A DEAL PIC</p>
              <button data-testid="pic-composer-close" onClick={onClose} className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center"><X className="w-5 h-5 text-slate-300" /></button>
            </div>
            <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Sales, discounts, garage sales — snap it & share it with local POG'RS. +5 POG BUCKS! 🎯</p>

            <label data-testid="pic-file-label" className={`mt-4 flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed cursor-pointer transition-colors overflow-hidden ${file ? "border-emerald-400/60 bg-emerald-500/10 h-44" : "border-slate-600 hover:border-amber-400/60 h-24"}`}>
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <>
                  <Camera className="w-6 h-6 text-slate-400" />
                  <span className="text-xs font-bold text-slate-300 px-4">Tap to snap or pick a photo</span>
                </>
              )}
              <input data-testid="pic-file-input" type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => pick(e.target.files?.[0])} />
            </label>

            <input data-testid="pic-caption-input" value={caption} maxLength={80} onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption — e.g. Huge markdowns at Elm St Market!"
              className="mt-3 w-full h-11 rounded-xl bg-slate-800 border border-slate-700 focus:border-amber-400/60 outline-none px-3 text-sm font-semibold placeholder:text-slate-500" />
            <input data-testid="pic-deal-input" value={deal} maxLength={70} onChange={(e) => setDeal(e.target.value)}
              placeholder="Deal line — e.g. 50% off everything 🔥 (optional)"
              className="mt-2 w-full h-11 rounded-xl bg-slate-800 border border-slate-700 focus:border-amber-400/60 outline-none px-3 text-sm font-semibold placeholder:text-slate-500" />

            <button data-testid="pic-post-submit" onClick={post} disabled={busy}
              className="mt-4 w-full h-12 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-heading font-black text-sm tracking-widest disabled:opacity-60 active:scale-95 transition-transform flex items-center justify-center gap-2">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> POSTING…</>) : "🎈 POG THIS PIC · +5 🎯"}
            </button>
            <p className="text-[10px] text-slate-500 font-bold text-center mt-2">Pics ride the feed for 14 days · your location pins the deal</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
